import type { GameStatsDebugDbRowResponse } from "libs/apiClient";
import client from "libs/client";
import { useMemo, useState, type CSSProperties } from "react";
import { useParams } from "react-router";
import NotFoundPage from "./errorPages/NotFoundPage";
import SkeletonBox from "ui-kit/src/Skeleton/SkeletonBox";

const cellBorder: CSSProperties = { border: "1px solid #ccc", padding: 6, fontSize: 12, verticalAlign: "top" };
const headerCell: CSSProperties = { ...cellBorder, background: "#f0f0f0", fontWeight: 600 };

const rowBgStatsMatch: CSSProperties = { backgroundColor: "#e8f5e9" };
const rowBgStatsMissing: CSSProperties = { backgroundColor: "#fff8e1" };
const rowBgStatsMismatch: CSSProperties = { backgroundColor: "#ffe0b2" };

function statsComparisonRowStyle(c: { match: boolean; missingInTest: boolean }): CSSProperties {
	if (c.missingInTest) {
		return rowBgStatsMissing;
	}
	if (c.match) {
		return rowBgStatsMatch;
	}
	return rowBgStatsMismatch;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
	return typeof v === "object" && v !== null && !Array.isArray(v);
}

function normalizeStatKey(key: string): string {
	return key
		.replace(/([a-z0-9])([A-Z])/g, "$1_$2")
		.toLowerCase()
		.replace(/_/g, "");
}

const DB_STAT_KEY_TO_TEST_KEY: Record<string, string> = {
	goalsScrored: "goalScoredTotal",
	goalsScored: "goalScoredTotal",
	goals: "goalScoredTotal",
	goalsOwn: "goalOwnTotal",
	shots: "shotTotal",
	fouls: "foulCommittedTotal",
	offsides: "offsideTotal",
	freeKicks: "freeKickTotal",
	freekicks: "freeKickTotal",
	corners: "cornerKickTotal",
	throwIns: "throwInTotal",
	yellowCards: "yellowCardTotal",
	redCards: "redCardTotal",
	assists: "assistTotal",
	interceptions: "interceptionTotal",
	clearances: "clearanceTotal",
	recoveries: "recoveryTotal",
	blocks: "blockTotal",
	mistakes: "mistakeTotal",
	foulsWon: "foulWonTotal",
	keyPasses: "passKeyTotal",
	shotsOnTarget: "shotOnTargetTotal",
	blockedShots: "shotBlockedTotal",
	shotsOffTarget: "shotOffTargetTotal",
	shotsInsidePa: "shotInsidePaTotal",
	shotsOutsidePa: "shotOutsidePaTotal",
	takeOnsSuccessful: "takeOnSuccess",
	takeOnsTotal: "takeOnTotal",
	tacklesSuccess: "tackleSuccess",
	tacklesTotal: "tackleTotal",
	aerialDuelsSuccess: "aerialDuelSuccess",
	aerialDuelsTotal: "aerialDuelTotal",
	groundDuelsSuccess: "groundDuelSuccess",
	groundDuelsTotal: "groundDuelTotal",
	passesSuccess: "passSuccess",
	passesTotal: "passTotal",
	crossesSuccess: "crossSuccess",
	crossesTotal: "crossTotal",
	passesForwardSuccess: "passForwardSuccess",
	passesForwardTotal: "passForwardTotal",
	passesSidewaysSuccess: "passSidewaysSuccess",
	passesSidewaysTotal: "passSidewaysTotal",
	passesBackwardSuccess: "passBackwardSuccess",
	passesBackwardTotal: "passBackwardTotal",
	longPassesSuccess: "passLongSuccess",
	longPassesTotal: "passLongTotal",
	mediumRangePassesSuccess: "passMediumSuccess",
	mediumRangePassesTotal: "passMediumTotal",
	shortPassesSuccess: "passShortSuccess",
	shortPassesTotal: "passShortTotal",
	goalsConceded: "goalConcededTotal",
	goalsKicksSuccess: "goalKickSuccess",
	goalsKicksTotal: "goalKickTotal",
	goalKicksSuccess: "goalKickSuccess",
	goalKicksTotal: "goalKickTotal",
	catches: "saveCatchTotal",
	punches: "saveBlockOrParryTotal",
	aerialClearancesSuccess: "goalkeeperInterceptionSuccess",
	aerialClearancesTotal: "goalkeeperInterceptionTotal",
	goalkeeperInterceptionsSuccess: "goalkeeperInterceptionSuccess",
	goalkeeperInterceptionsTotal: "goalkeeperInterceptionTotal",
	throwsSuccess: "throwsSuccess",
	throwsTotal: "throwsTotal",
	passLengthTotalM: "passLengthTotalM",
	passLengthCount: "passLengthCount",
	defensiveActionsOpaTotal: "defensiveActionsOpaTotal",
	defActionDistanceTotalM: "defActionDistanceTotalM",
	defActionDistanceCount: "defActionDistanceCount",
	crossesFacedTotal: "crossesFacedTotal",
	crossesStoppedTotal: "crossesStoppedTotal",
	shotsOnTargetFacedTotal: "shotsOnTargetFacedTotal",
};

function candidateTestKeysForDbKey(dbKey: string): string[] {
	const out: string[] = [];
	const fromMap = DB_STAT_KEY_TO_TEST_KEY[dbKey];
	if (fromMap !== undefined) {
		out.push(fromMap);
	}

	const third = dbKey.match(/^passesIn(Final|Middle|Defensive)Third(Success|Total)$/);
	if (third !== null) {
		out.push(`pass${third[1]}Third${third[2]}`);
	}

	return out;
}

function getTeamStatsBlob(statsTest: unknown, gameTeamId: number): Record<string, unknown> | null {
	if (!isPlainObject(statsTest)) {
		return null;
	}
	const teams = statsTest.teams;
	if (!Array.isArray(teams)) {
		return null;
	}
	for (const t of teams) {
		if (!isPlainObject(t)) {
			continue;
		}
		if (t.gameTeamId !== gameTeamId) {
			continue;
		}
		const stats = isPlainObject(t.stats) ? (t.stats as Record<string, unknown>) : {};
		return { ...stats };
	}
	return null;
}

function getPlayerStatsBlob(
	statsTest: unknown,
	gameTeamId: number,
	gamePlayerId: number,
): Record<string, unknown> | null {
	if (!isPlainObject(statsTest)) {
		return null;
	}
	const players = statsTest.players;
	if (!Array.isArray(players)) {
		return null;
	}
	for (const p of players) {
		if (!isPlainObject(p)) {
			continue;
		}
		if (p.gameTeamId === gameTeamId && p.gamePlayerId === gamePlayerId && isPlainObject(p.stats)) {
			return p.stats as Record<string, unknown>;
		}
	}
	return null;
}

function resolveTestStatsBlob(statsTest: unknown, row: GameStatsDebugDbRowResponse): Record<string, unknown> | null {
	const { segment, gameTeamId, gamePlayerId } = row;
	if (segment.startsWith("game_player")) {
		if (gameTeamId === undefined || gameTeamId === null || gamePlayerId === undefined || gamePlayerId === null) {
			return null;
		}
		return getPlayerStatsBlob(statsTest, gameTeamId, gamePlayerId);
	}
	if (segment.startsWith("game_team")) {
		if (gameTeamId === undefined || gameTeamId === null) {
			return null;
		}
		return getTeamStatsBlob(statsTest, gameTeamId);
	}
	return null;
}

function findInStatsBlob(
	statsBlob: Record<string, unknown> | null,
	dbKey: string,
): { testKey: string | null; value: unknown } {
	if (statsBlob === null) {
		return { testKey: null, value: undefined };
	}
	if (Object.prototype.hasOwnProperty.call(statsBlob, dbKey)) {
		return { testKey: dbKey, value: statsBlob[dbKey] };
	}
	for (const cand of candidateTestKeysForDbKey(dbKey)) {
		if (Object.prototype.hasOwnProperty.call(statsBlob, cand)) {
			return { testKey: cand, value: statsBlob[cand] };
		}
	}
	const want = normalizeStatKey(dbKey);
	for (const [k, v] of Object.entries(statsBlob)) {
		if (normalizeStatKey(k) === want) {
			return { testKey: k, value: v };
		}
	}
	return { testKey: null, value: undefined };
}

function formatCellValue(v: unknown): string {
	if (v === null || v === undefined) {
		return "—";
	}
	if (typeof v === "object") {
		return JSON.stringify(v);
	}
	return String(v);
}

function formatPassRatioCell(testKey: string | null, testVal: unknown): string | null {
	if (testKey !== "passRatio") {
		return null;
	}
	if (testVal === null || testVal === undefined) {
		return null;
	}
	let n: number;
	if (typeof testVal === "number") {
		if (!Number.isFinite(testVal)) {
			return null;
		}
		n = testVal;
	} else if (typeof testVal === "string") {
		n = Number(testVal.trim());
		if (!Number.isFinite(n)) {
			return null;
		}
	} else {
		return null;
	}
	const pct = n > 0 && n <= 1 ? n * 100 : n;
	return `${pct.toFixed(2)}%`;
}

function formatStatsTestCell(_kind: "team" | "player", testKey: string | null, testVal: unknown, missingInTest: boolean): string {
	if (missingInTest) {
		return "—";
	}
	const passRatioPct = formatPassRatioCell(testKey, testVal);
	if (passRatioPct !== null) {
		return passRatioPct;
	}
	return formatCellValue(testVal);
}

function alignPossessionForCompare(dbKey: string, dbVal: unknown, testVal: unknown): [unknown, unknown] {
	if (normalizeStatKey(dbKey) !== "possession") {
		return [dbVal, testVal];
	}
	if (typeof dbVal !== "number" || typeof testVal !== "number") {
		return [dbVal, testVal];
	}
	if (dbVal > 1 && testVal <= 1) {
		return [dbVal / 100, testVal];
	}
	if (dbVal <= 1 && testVal > 1) {
		return [dbVal, testVal / 100];
	}
	return [dbVal, testVal];
}

function toWholePercentPass(v: unknown): number | null {
	if (v === null || v === undefined) {
		return null;
	}
	if (typeof v === "number" && Number.isFinite(v)) {
		if (v > 0 && v <= 1) {
			return Math.round(v * 100);
		}
		return Math.round(v);
	}
	if (typeof v === "string") {
		const n = Number(v.trim());
		if (!Number.isNaN(n) && Number.isFinite(n)) {
			if (n > 0 && n <= 1) {
				return Math.round(n * 100);
			}
			return Math.round(n);
		}
	}
	return null;
}

function alignPassAccuracyForCompare(dbKey: string, dbVal: unknown, testVal: unknown): [unknown, unknown] {
	if (normalizeStatKey(dbKey) !== "passaccuracy") {
		return [dbVal, testVal];
	}
	const dbPct = toWholePercentPass(dbVal);
	const testPct = toWholePercentPass(testVal);
	if (dbPct !== null && testPct !== null) {
		return [dbPct, testPct];
	}
	return [dbVal, testVal];
}

function resolveTestForPassAccuracy(
	statsBlob: Record<string, unknown> | null,
): { testKey: string | null; value: unknown } {
	if (statsBlob === null) {
		return { testKey: null, value: undefined };
	}
	if (Object.prototype.hasOwnProperty.call(statsBlob, "passRatio")) {
		return { testKey: "passRatio", value: statsBlob.passRatio };
	}
	return { testKey: null, value: undefined };
}

function findTestValueForDbKey(statsBlob: Record<string, unknown> | null, dbKey: string): { testKey: string | null; value: unknown } {
	if (normalizeStatKey(dbKey) === "passaccuracy") {
		return resolveTestForPassAccuracy(statsBlob);
	}
	return findInStatsBlob(statsBlob, dbKey);
}

function valuesMatchForStats(dbKey: string, db: unknown, test: unknown): boolean {
	let preA = db;
	let preB = test;
	const [pa, pb] = alignPassAccuracyForCompare(dbKey, preA, preB);
	preA = pa;
	preB = pb;
	const [a, b] = alignPossessionForCompare(dbKey, preA, preB);
	if (a === b) {
		return true;
	}
	if (a === null || a === undefined) {
		return b === null || b === undefined;
	}
	if (b === null || b === undefined) {
		return false;
	}
	if (typeof a === "number" && typeof b === "number") {
		if (Number.isNaN(a) && Number.isNaN(b)) {
			return true;
		}
		return Math.abs(a - b) < 1e-6;
	}
	if (typeof a === "number" && typeof b === "string") {
		const n = Number(b);
		return !Number.isNaN(n) && Math.abs(a - n) < 1e-6;
	}
	if (typeof a === "string" && typeof b === "number") {
		const n = Number(a);
		return !Number.isNaN(n) && Math.abs(n - b) < 1e-6;
	}
	if (typeof a === "boolean" && typeof b === "string") {
		return (b === "true" && a) || (b === "false" && !a);
	}
	if (typeof a === "string" && typeof b === "boolean") {
		return (a === "true" && b) || (a === "false" && !b);
	}
	if (typeof a === "object" && typeof b === "object") {
		return JSON.stringify(a) === JSON.stringify(b);
	}
	return false;
}

type StatCategory = "basic" | "offensive" | "defensive";

function segmentToCategory(segment: string): StatCategory {
	if (segment.includes("offensive")) {
		return "offensive";
	}
	if (segment.includes("defensive") || segment.includes("goalkeeper")) {
		return "defensive";
	}
	return "basic";
}

const PLAYER_HEADER_DATA_KEYS = new Set([
	"playerId",
	"npPlayerId",
	"playerName",
	"backNumber",
	"position",
	"minutesPlayed",
	"rating",
]);

function msToMinSecLabel(ms: unknown): string {
	if (typeof ms !== "number" || !Number.isFinite(ms)) {
		return "";
	}
	const totalSec = Math.floor(ms / 1000);
	const m = Math.floor(totalSec / 60);
	const s = totalSec % 60;
	return `${m}:${String(s).padStart(2, "0")}`;
}

function shouldIncludeDataKey(kind: "team" | "player", dbKey: string): boolean {
	if (kind === "player" && PLAYER_HEADER_DATA_KEYS.has(dbKey)) {
		return false;
	}
	return true;
}

function statRowMatchesFilters(
	c: { key: string; testKey: string | null; match: boolean },
	onlyDifferent: boolean,
	dbKeySubstr: string,
	testKeySubstr: string,
): boolean {
	if (onlyDifferent && c.match) {
		return false;
	}
	const dbF = dbKeySubstr.trim().toLowerCase();
	if (dbF !== "" && !c.key.toLowerCase().includes(dbF)) {
		return false;
	}
	const testF = testKeySubstr.trim().toLowerCase();
	if (testF !== "" && !(c.testKey ?? "").toLowerCase().includes(testF)) {
		return false;
	}
	return true;
}

function playerBlockHeaderLine(rows: GameStatsDebugDbRowResponse[]): string {
	const base = rows.find((r) => r.segment === "game_player_stat");
	const d = base?.data;
	if (d === undefined || !isPlainObject(d)) {
		return "";
	}
	const parts: string[] = [];
	if (d.backNumber != null) {
		parts.push(`#${String(d.backNumber)}`);
	}
	const minLabel = msToMinSecLabel(d.minutesPlayed);
	if (minLabel !== "") {
		parts.push(`${minLabel} min`);
	}
	if (d.position != null && d.position !== "") {
		parts.push(String(d.position));
	}
	return parts.join(" · ");
}

const filterInputStyle: CSSProperties = { padding: "6px 8px", fontSize: 13, minWidth: 180, maxWidth: "100%" };
const filterLabelStyle: CSSProperties = { fontSize: 13, display: "flex", flexDirection: "column", gap: 4, minWidth: 0 };

export function GameStatsDebugPage() {
	const { gameId: gameIdParam } = useParams();
	const gameId = gameIdParam === undefined ? NaN : parseInt(gameIdParam, 10);
	const [onlyDifferentValues, setOnlyDifferentValues] = useState(false);
	const [dbKeyFilter, setDbKeyFilter] = useState("");
	const [testKeyFilter, setTestKeyFilter] = useState("");
	const { data: response, isLoading, error } = client.getGameStatsDebug.useQuery(
		{ gameId },
		{ enabled: Number.isFinite(gameId) && gameId > 0, queryKey: ["gameStatsDebug", gameId] },
	);
	const payload = response?.data;

	const mergedBlocks = useMemo(() => {
		if (payload === undefined) {
			return [];
		}
		const statsTest = payload.statsTest;
		const teamGroups = new Map<number, GameStatsDebugDbRowResponse[]>();
		const playerGroups = new Map<string, GameStatsDebugDbRowResponse[]>();

		for (const row of payload.dbRows) {
			if (row.segment.startsWith("game_team")) {
				const tid = row.gameTeamId;
				if (tid === undefined || tid === null) {
					continue;
				}
				const list = teamGroups.get(tid);
				if (list === undefined) {
					teamGroups.set(tid, [row]);
				} else {
					list.push(row);
				}
				continue;
			}
			if (row.segment.startsWith("game_player")) {
				const tid = row.gameTeamId;
				const pid = row.gamePlayerId;
				if (tid === undefined || tid === null || pid === undefined || pid === null) {
					continue;
				}
				const gk = `${tid}:${pid}`;
				const list = playerGroups.get(gk);
				if (list === undefined) {
					playerGroups.set(gk, [row]);
				} else {
					list.push(row);
				}
			}
		}

		type ComparisonRow = {
			category: StatCategory;
			key: string;
			dbVal: unknown;
			testKey: string | null;
			testVal: unknown;
			match: boolean;
			missingInTest: boolean;
		};

		type MergedBlock = {
			blockKey: string;
			kind: "team" | "player";
			gameTeamId: number;
			gamePlayerId: number | null;
			subtitle: string;
			statsBlob: Record<string, unknown> | null;
			comparisons: ComparisonRow[];
		};

		const blocks: MergedBlock[] = [];

		const sortedTeamIds = Array.from(teamGroups.keys()).sort((a, b) => a - b);
		for (const tid of sortedTeamIds) {
			const rows = teamGroups.get(tid);
			if (rows === undefined || rows.length === 0) {
				continue;
			}
			const probe = rows[0];
			const statsBlob =
				statsTest === undefined || statsTest === null ? null : resolveTestStatsBlob(statsTest, probe);
			const comparisons: ComparisonRow[] = [];
			for (const row of rows) {
				const category = segmentToCategory(row.segment);
				for (const key of Object.keys(row.data)) {
					if (!shouldIncludeDataKey("team", key)) {
						continue;
					}
					const dbVal = row.data[key];
					const { testKey, value: testVal } = findTestValueForDbKey(statsBlob, key);
					const missingInTest = testKey === null;
					const match = missingInTest ? false : valuesMatchForStats(key, dbVal, testVal);
					comparisons.push({ category, key, dbVal, testKey, testVal, match, missingInTest });
				}
			}
			comparisons.sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true, sensitivity: "base" }));
			blocks.push({
				blockKey: `team-${tid}`,
				kind: "team",
				gameTeamId: tid,
				gamePlayerId: null,
				subtitle: `Team · gameTeamId ${tid}`,
				statsBlob,
				comparisons,
			});
		}

		const sortedPlayerKeys = Array.from(playerGroups.keys()).sort((a, b) => {
			const [ta, pa] = a.split(":").map(Number);
			const [tb, pb] = b.split(":").map(Number);
			if (ta !== tb) {
				return ta - tb;
			}
			return pa - pb;
		});
		for (const gk of sortedPlayerKeys) {
			const rows = playerGroups.get(gk);
			if (rows === undefined || rows.length === 0) {
				continue;
			}
			const [tidStr, pidStr] = gk.split(":");
			const tid = Number(tidStr);
			const pid = Number(pidStr);
			const probe = rows[0];
			const statsBlob =
				statsTest === undefined || statsTest === null ? null : resolveTestStatsBlob(statsTest, probe);
			const comparisons: ComparisonRow[] = [];
			for (const row of rows) {
				const category = segmentToCategory(row.segment);
				for (const key of Object.keys(row.data)) {
					if (!shouldIncludeDataKey("player", key)) {
						continue;
					}
					const dbVal = row.data[key];
					const { testKey, value: testVal } = findTestValueForDbKey(statsBlob, key);
					const missingInTest = testKey === null;
					const match = missingInTest ? false : valuesMatchForStats(key, dbVal, testVal);
					comparisons.push({ category, key, dbVal, testKey, testVal, match, missingInTest });
				}
			}
			comparisons.sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true, sensitivity: "base" }));
			const metaLine = playerBlockHeaderLine(rows);
			blocks.push({
				blockKey: `player-${tid}-${pid}`,
				kind: "player",
				gameTeamId: tid,
				gamePlayerId: pid,
				subtitle:
					metaLine !== ""
						? `Player · gamePlayerId ${pid} · gameTeamId ${tid} — ${metaLine}`
						: `Player · gamePlayerId ${pid} · gameTeamId ${tid}`,
				statsBlob,
				comparisons,
			});
		}

		return blocks;
	}, [payload]);

	if (!Number.isFinite(gameId) || gameId <= 0) {
		return <NotFoundPage />;
	}

	if (error) {
		const code = error?.response?.status;
		if (code === 404) {
			return <NotFoundPage />;
		}
		return (
			<div style={{ padding: 24 }}>
				<p>Failed to load ({code ?? "?"}).</p>
			</div>
		);
	}

	return (
		<div style={{ padding: "24px 32px", maxWidth: "100%" }}>
			<h1 style={{ marginBottom: 16, fontSize: 22 }}>Game stats debug — game {gameId}</h1>
			<p style={{ marginBottom: 16, color: "#555", maxWidth: 920 }}>
				Compares values from <code>eb_game_team_*_stat</code> and <code>eb_game_player_*_stat</code> with the JSON in{" "}
				<code>eb_game_stats_test.data</code>. Test shape: a <code>teams[]</code> array with <code>gameTeamId</code> and{" "}
				<code>stats</code>, and a <code>players[]</code> array with <code>gameTeamId</code>, <code>gamePlayerId</code>, and{" "}
				<code>stats</code>. For each DB row, the corresponding <code>stats</code> object is resolved; keys are matched across
				different spellings (camelCase vs. snake_case), known aliases (e.g. <code>goals</code> ↔{" "}
				<code>goalScoredTotal</code>, <code>blockedShots</code> ↔ <code>shotBlockedTotal</code>,{" "}
				<code>passesInFinalThirdTotal</code> ↔{" "}
				<code>passFinalThirdTotal</code>), and minor pluralization differences. For <code>possession</code>, the compare logic
				accepts both fractional and percentage representations on either side. DB fields with no match in stats test (e.g.
				player name) are not shown in the stats test column; team <code>averageRating</code> is not emitted by the stats
				calculator JSON (UI derives it from player ratings). The comparison is indicated by row background
				color only (same rules as game events debug). For <code>passAccuracy</code>, stats test resolves to{" "}
				<code>passRatio</code>; both sides are compared as whole percents (<code>passRatio</code> values in the (0, 1] range
				are converted via <code>round(ratio * 100)</code>). For teams, matched <code>passRatio</code> is displayed in the
				stats test column as a percentage with two decimal places (e.g.{" "}
				<code>85.67%</code>).
			</p>
			{!isLoading && payload !== undefined ? (
				<div
					style={{
						marginBottom: 20,
						padding: 12,
						border: "1px solid #ddd",
						borderRadius: 6,
						background: "#fafafa",
						display: "flex",
						flexWrap: "wrap",
						gap: "12px 24px",
						alignItems: "flex-end",
					}}
				>
					<label style={{ ...filterLabelStyle, flexDirection: "row", alignItems: "center", gap: 8 }}>
						<input
							type="checkbox"
							checked={onlyDifferentValues}
							onChange={(e) => {
								setOnlyDifferentValues(e.target.checked);
							}}
						/>
						<span style={{ fontSize: 13 }}>Only rows with differing values</span>
					</label>
					<label style={filterLabelStyle}>
						<span>Key name (DB)</span>
						<input
							type="search"
							value={dbKeyFilter}
							onChange={(e) => {
								setDbKeyFilter(e.target.value);
							}}
							placeholder="Contains…"
							style={filterInputStyle}
						/>
					</label>
					<label style={filterLabelStyle}>
						<span>Key name (test)</span>
						<input
							type="search"
							value={testKeyFilter}
							onChange={(e) => {
								setTestKeyFilter(e.target.value);
							}}
							placeholder="Contains…"
							style={filterInputStyle}
						/>
					</label>
				</div>
			) : null}
			{isLoading ? <SkeletonBox style={{ width: "100%", height: 200 }} /> : null}
			{!isLoading && payload !== undefined ? (
				<>
					<div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
						{mergedBlocks.map(({ blockKey, kind, subtitle, statsBlob, comparisons }) => {
							const filteredComparisons = comparisons.filter((c) =>
								statRowMatchesFilters(c, onlyDifferentValues, dbKeyFilter, testKeyFilter),
							);
							const blobLabel =
								statsBlob === null
									? payload.statsTest == null
										? "no stats test"
										: "no teams[]/players[] entry for this gameTeamId / gamePlayerId"
									: `stats object found (${Object.keys(statsBlob).length} keys)`;
							return (
								<section key={blockKey}>
									<h2 style={{ fontSize: 15, margin: "0 0 8px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
										<span style={{ fontWeight: 600 }}>{subtitle}</span>
										<span style={{ fontSize: 12, color: "#666" }}>({blobLabel})</span>
									</h2>
									<div style={{ overflowX: "auto" }}>
										<table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
											<thead>
												<tr>
													<th style={{ ...headerCell, width: "10%" }}>category</th>
													<th style={{ ...headerCell, width: "16%" }}>Key (DB)</th>
													<th style={{ ...headerCell, width: "24%" }}>DB</th>
													<th style={{ ...headerCell, width: "14%" }}>Key (stats test)</th>
													<th style={{ ...headerCell, width: "36%" }}>Stats test</th>
												</tr>
											</thead>
											<tbody>
												{filteredComparisons.length === 0 ? (
													<tr>
														<td colSpan={5} style={{ ...cellBorder, color: "#666", fontStyle: "italic" }}>
															No rows match the filter.
														</td>
													</tr>
												) : (
													filteredComparisons.map((c) => (
														<tr key={`${c.category}-${c.key}`} style={statsComparisonRowStyle(c)}>
															<td style={cellBorder}>
																<code>{c.category}</code>
															</td>
															<td style={cellBorder}>
																<code>{c.key}</code>
															</td>
															<td style={{ ...cellBorder, wordBreak: "break-word" }}>{formatCellValue(c.dbVal)}</td>
															<td style={{ ...cellBorder, wordBreak: "break-word" }}>
																{c.testKey === null ? "—" : <code>{c.testKey}</code>}
															</td>
															<td style={{ ...cellBorder, wordBreak: "break-word" }}>
																{formatStatsTestCell(kind, c.testKey, c.testVal, c.missingInTest)}
															</td>
														</tr>
													))
												)}
											</tbody>
										</table>
									</div>
								</section>
							);
						})}
					</div>
				</>
			) : null}
		</div>
	);
}
